import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/vue-query'

import {
  isExperienceQueryPartitionReady,
  prefixExperienceQueryKey,
  type ExperienceQueryPartition,
} from '@common/experience-runtime/utils/create-experience-query-partition'

import { EventsService } from '../services/events.service'
import { applySavedSessionFlag } from '../utils/apply-saved-session-flag'
import { eventsQueryKeys } from '../utils/events-query-keys'
import { mapPortalEventsByState } from '../utils/map-portal-event'
import type { EventsHubCard } from '../types/events-hub'
import type { EventsDirectoryListKind } from '../types/events-directory-experience-controller'
import type {
  EventsAttendeesByContactResponse,
  EventsManagementAttendeesResponse,
  EventsManagementDetailResponse,
  EventsManagementExhibitorMembersResponse,
  EventsManagementExhibitorsResponse,
  EventsManagementSavedSessionParams,
  EventsManagementScheduleResponse,
  EventsManagementSpeakersResponse,
  EventsPortalStatePage,
} from '../types/events-management'

export const EVENTS_DIRECTORY_PAGE_SIZE = 12
export const EVENTS_ATTENDEES_PAGE_LIMIT = 20
export const EVENTS_TICKETS_PAGE_LIMIT = 20
export const EVENTS_EXHIBITORS_PAGE_LIMIT = 20

export interface EventsPortalBucket {
  readonly items: EventsHubCard[]
  readonly total: number
}

export interface EventsPortalBuckets {
  readonly live: EventsPortalBucket
  readonly upcoming: EventsPortalBucket
  readonly past: EventsPortalBucket
}

const toPortalBucket = (page: EventsPortalStatePage, state: EventsDirectoryListKind): EventsPortalBucket => ({
  items: mapPortalEventsByState(page, state),
  total: page.pagination.total,
})

const selectPortalDirectoryPage = (
  response: { readonly live: EventsPortalStatePage; readonly upcoming: EventsPortalStatePage; readonly past: EventsPortalStatePage },
  listKind: EventsDirectoryListKind
): EventsPortalStatePage => {
  if (listKind === 'live') return response.live
  if (listKind === 'past') return response.past
  return response.upcoming
}

export const useEventsQueryStore = (queryKeyPrefix?: MaybeRefOrGetter<ExperienceQueryPartition | undefined>) => {
  const service = new EventsService()
  const queryClient = useQueryClient()
  const queryKeys = eventsQueryKeys
  const scopedQueryKey = <TQueryKey extends readonly unknown[]>(queryKey: TQueryKey) => prefixExperienceQueryKey(queryKey, queryKeyPrefix)
  const isPartitionReady = () => isExperienceQueryPartitionReady(queryKeyPrefix)

  const fetchPortalEventsQuery = (locationId: MaybeRefOrGetter<string | undefined>, contactId: MaybeRefOrGetter<string | undefined>) => {
    return useQuery<EventsPortalBuckets, Error>({
      queryKey: computed(() => scopedQueryKey(queryKeys.portal(toValue(locationId), toValue(contactId)?.trim() || undefined))),
      queryFn: async () => {
        const resolvedLocationId = toValue(locationId)?.trim()
        const resolvedContactId = toValue(contactId)?.trim()
        if (!resolvedLocationId) {
          throw new Error('Location ID is required for fetching portal events')
        }
        if (!resolvedContactId) {
          throw new Error('Contact ID is required for fetching portal events')
        }
        const response = await service.getPortalEvents({
          limit: 50,
        })
        return {
          live: toPortalBucket(response.live, 'live'),
          upcoming: toPortalBucket(response.upcoming, 'upcoming'),
          past: toPortalBucket(response.past, 'past'),
        }
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(locationId)?.trim()) && Boolean(toValue(contactId)?.trim()) && isPartitionReady()),
    })
  }

  const fetchPortalDirectoryQuery = (
    listKind: MaybeRefOrGetter<EventsDirectoryListKind>,
    locationId: MaybeRefOrGetter<string | undefined>,
    contactId: MaybeRefOrGetter<string | undefined>
  ) => {
    const infiniteQuery = useInfiniteQuery<
      EventsPortalStatePage,
      Error,
      InfiniteData<EventsPortalStatePage, string>,
      readonly unknown[],
      string
    >({
      queryKey: computed(() =>
        scopedQueryKey(queryKeys.portalDirectory(toValue(listKind), toValue(locationId), toValue(contactId)?.trim() || undefined))
      ),
      queryFn: async ({ pageParam }) => {
        const resolvedLocationId = toValue(locationId)?.trim()
        const resolvedContactId = toValue(contactId)?.trim()
        if (!resolvedLocationId) {
          throw new Error('Location ID is required for fetching portal events')
        }
        if (!resolvedContactId) {
          throw new Error('Contact ID is required for fetching portal events')
        }
        const state = toValue(listKind)
        const response = await service.getPortalEvents({
          state,
          ...(pageParam ? { cursor: pageParam } : {}),
          limit: EVENTS_DIRECTORY_PAGE_SIZE,
        })
        return selectPortalDirectoryPage(response, state)
      },
      initialPageParam: '',
      getNextPageParam: lastPage => lastPage.pagination.nextCursor,
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(locationId)?.trim()) && Boolean(toValue(contactId)?.trim()) && isPartitionReady()),
    })

    const allData = computed(() => (infiniteQuery.data.value?.pages ?? []).flatMap(page => mapPortalEventsByState(page, toValue(listKind))))

    return {
      ...infiniteQuery,
      allData,
    }
  }

  const fetchEventTicketsQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    contactId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>
  ) => {
    const infiniteQuery = useInfiniteQuery<
      EventsAttendeesByContactResponse,
      Error,
      InfiniteData<EventsAttendeesByContactResponse, string>,
      readonly unknown[],
      string
    >({
      queryKey: computed(() =>
        scopedQueryKey(
          queryKeys.attendeesByContact(toValue(eventId), toValue(contactId)?.trim() || undefined, toValue(locationId)?.trim() || undefined)
        )
      ),
      queryFn: async ({ pageParam }) => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedContactId = toValue(contactId)?.trim()
        const resolvedLocationId = toValue(locationId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching tickets')
        }
        if (!resolvedContactId) {
          throw new Error('Contact ID is required for fetching tickets')
        }
        if (!resolvedLocationId) {
          throw new Error('Location ID is required for fetching tickets')
        }
        return service.getEventTickets({
          eventId: resolvedEventId,
          ...(pageParam ? { cursor: pageParam } : {}),
          limit: EVENTS_TICKETS_PAGE_LIMIT,
        })
      },
      initialPageParam: '',
      getNextPageParam: lastPage => lastPage.pagination.nextCursor,
      staleTime: 5 * 60 * 1000,
      enabled: computed(
        () =>
          Boolean(toValue(eventId)?.trim()) &&
          Boolean(toValue(contactId)?.trim()) &&
          Boolean(toValue(locationId)?.trim()) &&
          isPartitionReady()
      ),
    })

    const allData = computed(() => infiniteQuery.data.value?.pages.flatMap(page => page.attendees) ?? [])

    return {
      ...infiniteQuery,
      allData,
    }
  }

  const fetchEventDetailQuery = (eventId: MaybeRefOrGetter<string | undefined>) => {
    return useQuery<EventsManagementDetailResponse, Error>({
      queryKey: computed(() => scopedQueryKey(queryKeys.detail(toValue(eventId)))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event detail')
        }
        return service.getPublicEvent(resolvedEventId)
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && isPartitionReady()),
    })
  }

  const fetchEventScheduleQuery = (eventId: MaybeRefOrGetter<string | undefined>, contactId: MaybeRefOrGetter<string | undefined>) => {
    return useQuery<EventsManagementScheduleResponse, Error>({
      queryKey: computed(() => scopedQueryKey(queryKeys.schedule(toValue(eventId), toValue(contactId)?.trim() || undefined))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedContactId = toValue(contactId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event schedule')
        }
        if (!resolvedContactId) {
          throw new Error('Contact ID is required for fetching event schedule')
        }
        return service.getPublicEventSchedule({
          eventId: resolvedEventId,
          contactId: resolvedContactId,
        })
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && Boolean(toValue(contactId)?.trim()) && isPartitionReady()),
    })
  }

  const fetchEventSpeakerScheduleQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    contactId: MaybeRefOrGetter<string | undefined>,
    speakerId: MaybeRefOrGetter<string | undefined>
  ) => {
    return useQuery<EventsManagementScheduleResponse, Error>({
      queryKey: computed(() =>
        scopedQueryKey(
          queryKeys.scheduleBySpeaker(toValue(eventId), toValue(contactId)?.trim() || undefined, toValue(speakerId)?.trim() || undefined)
        )
      ),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedContactId = toValue(contactId)?.trim()
        const resolvedSpeakerId = toValue(speakerId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event schedule')
        }
        if (!resolvedContactId) {
          throw new Error('Contact ID is required for fetching event schedule')
        }
        if (!resolvedSpeakerId) {
          throw new Error('Speaker ID is required for fetching speaker schedule')
        }
        return service.getPublicEventSchedule({
          eventId: resolvedEventId,
          contactId: resolvedContactId,
          speakerId: resolvedSpeakerId,
        })
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(
        () =>
          Boolean(toValue(eventId)?.trim()) &&
          Boolean(toValue(contactId)?.trim()) &&
          Boolean(toValue(speakerId)?.trim()) &&
          isPartitionReady()
      ),
    })
  }

  const fetchEventSpeakersQuery = (eventId: MaybeRefOrGetter<string | undefined>) => {
    return useQuery<EventsManagementSpeakersResponse, Error>({
      queryKey: computed(() => scopedQueryKey(queryKeys.speakers(toValue(eventId)))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event speakers')
        }
        return service.getPublicEventSpeakers(resolvedEventId)
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && isPartitionReady()),
    })
  }

  const fetchEventAttendeesQuery = (eventId: MaybeRefOrGetter<string | undefined>) => {
    const infiniteQuery = useInfiniteQuery<
      EventsManagementAttendeesResponse,
      Error,
      InfiniteData<EventsManagementAttendeesResponse, number>,
      readonly unknown[],
      number
    >({
      queryKey: computed(() => scopedQueryKey(queryKeys.attendees(toValue(eventId)))),
      queryFn: async ({ pageParam }) => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event attendees')
        }
        return service.getPublicEventAttendees(resolvedEventId, {
          page: pageParam,
          pageLimit: EVENTS_ATTENDEES_PAGE_LIMIT,
        })
      },
      initialPageParam: 1,
      getNextPageParam: lastPage => (lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined),
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && isPartitionReady()),
    })

    const allData = computed(() => infiniteQuery.data.value?.pages.flatMap(page => page.attendees) ?? [])

    return {
      ...infiniteQuery,
      allData,
    }
  }

  const fetchEventExhibitorsQuery = (eventId: MaybeRefOrGetter<string | undefined>, locationId: MaybeRefOrGetter<string | undefined>) => {
    const infiniteQuery = useInfiniteQuery<
      EventsManagementExhibitorsResponse,
      Error,
      InfiniteData<EventsManagementExhibitorsResponse, number>,
      readonly unknown[],
      number
    >({
      queryKey: computed(() => scopedQueryKey(queryKeys.exhibitors(toValue(eventId), toValue(locationId)?.trim() || undefined))),
      queryFn: async ({ pageParam }) => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedLocationId = toValue(locationId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching exhibitors')
        }
        if (!resolvedLocationId) {
          throw new Error('Location ID is required for fetching exhibitors')
        }
        return service.listExhibitors({
          eventId: resolvedEventId,
          locationId: resolvedLocationId,
          skip: pageParam,
          limit: EVENTS_EXHIBITORS_PAGE_LIMIT,
        })
      },
      initialPageParam: 0,
      getNextPageParam: lastPage => {
        const nextSkip = lastPage.pagination.skip + lastPage.exhibitors.length
        return nextSkip < lastPage.pagination.total ? nextSkip : undefined
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && Boolean(toValue(locationId)?.trim()) && isPartitionReady()),
    })

    const allData = computed(() => infiniteQuery.data.value?.pages.flatMap(page => page.exhibitors) ?? [])

    return {
      ...infiniteQuery,
      allData,
    }
  }

  const fetchEventExhibitorMembersQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    exhibitorId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>
  ) => {
    return useQuery<EventsManagementExhibitorMembersResponse, Error>({
      queryKey: computed(() =>
        scopedQueryKey(
          queryKeys.exhibitorMembers(toValue(eventId), toValue(exhibitorId)?.trim() || undefined, toValue(locationId)?.trim() || undefined)
        )
      ),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedExhibitorId = toValue(exhibitorId)?.trim()
        const resolvedLocationId = toValue(locationId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching exhibitor members')
        }
        if (!resolvedExhibitorId) {
          throw new Error('Exhibitor ID is required for fetching exhibitor members')
        }
        if (!resolvedLocationId) {
          throw new Error('Location ID is required for fetching exhibitor members')
        }
        return service.listExhibitorMembers({
          eventId: resolvedEventId,
          exhibitorId: resolvedExhibitorId,
          locationId: resolvedLocationId,
        })
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(
        () =>
          Boolean(toValue(eventId)?.trim()) &&
          Boolean(toValue(exhibitorId)?.trim()) &&
          Boolean(toValue(locationId)?.trim()) &&
          isPartitionReady()
      ),
    })
  }

  const updateSavedSessionMutation = useMutation({
    mutationFn: (variables: EventsManagementSavedSessionParams) => service.updatePublicEventSavedSession(variables),
    onMutate: async variables => {
      const queryKey = scopedQueryKey(queryKeys.schedule(variables.eventId, variables.contactId))
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<EventsManagementScheduleResponse>(queryKey)
      if (previous) {
        queryClient.setQueryData<EventsManagementScheduleResponse>(queryKey, {
          ...previous,
          sessions: applySavedSessionFlag(previous.sessions, variables.sessionId, variables.saved),
        })
      }
      return { previous, queryKey }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previous)
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scopedQueryKey(queryKeys.schedule(variables.eventId, variables.contactId)) })
    },
  })

  return {
    fetchPortalEventsQuery,
    fetchPortalDirectoryQuery,
    fetchEventDetailQuery,
    fetchEventScheduleQuery,
    fetchEventSpeakerScheduleQuery,
    fetchEventSpeakersQuery,
    fetchEventAttendeesQuery,
    fetchEventTicketsQuery,
    fetchEventExhibitorsQuery,
    fetchEventExhibitorMembersQuery,
    updateSavedSession: updateSavedSessionMutation.mutateAsync,
    queryKeys,
  }
}
